import React from 'react';
import { cn } from '../ui/utils';

interface DashboardLayoutProps {
  children: React.ReactNode;
  className?: string;
}

export function DashboardLayout({ children, className }: DashboardLayoutProps) {
  return (
    <div className={cn(
      'min-h-screen bg-neutral-50 p-4 md:p-6 lg:p-8',
      className
    )}>
      <div className="mx-auto max-w-7xl">
        {children}
      </div>
    </div>
  );
}

interface DashboardGridProps {
  children: React.ReactNode;
  className?: string;
}

export function DashboardGrid({ children, className }: DashboardGridProps) {
  return (
    <div className={cn(
      'grid grid-cols-1 gap-6 md:grid-cols-8 lg:grid-cols-12',
      className
    )}>
      {children}
    </div>
  );
}

interface DashboardCardProps {
  children: React.ReactNode;
  className?: string;
  span?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  };
}

const MOBILE_COL_SPANS: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};

const TABLET_COL_SPANS: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

const DESKTOP_COL_SPANS: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
};

export function DashboardCard({
  children,
  className,
  span = { mobile: 1, tablet: 4, desktop: 6 }
}: DashboardCardProps) {
  const mobileSpan = span.mobile ?? 1;
  const tabletSpan = span.tablet ?? mobileSpan;
  const desktopSpan = span.desktop ?? tabletSpan;

  const spanClasses = [
    MOBILE_COL_SPANS[mobileSpan] ?? MOBILE_COL_SPANS[1],
    TABLET_COL_SPANS[tabletSpan],
    DESKTOP_COL_SPANS[desktopSpan],
  ];

  return (
    <div className={cn(
      'rounded-2xl bg-white p-6 shadow-sm transition-shadow hover:shadow-md',
      spanClasses,
      className
    )}>
      {children}
    </div>
  );
}
